const getItemParam = (product: FlattenedProduct | ProductItemWithProductData): RequiredItemParam => ({
  itemId: mapConstructorItemId(product),
  itemName: product.productName || product.name || '',
  variationId: product.productId || product.id || '',
})

const noop = () => {}

export const useConstructorTracker = () => {
  if (!import.meta.client) {
    return {
      trackAutocompleteSelect: noop,
      trackBrowseResultClick: noop,
      trackBrowseResultsLoaded: noop,
      trackConversion: noop,
      trackWishlistConversion: noop,
      trackInputFocus: noop,
      trackItemDetailLoad: noop,
      trackPurchase: noop,
      trackRecommendationView: noop,
      trackRecommendationClick: noop,
      trackSearchResultClick: noop,
      trackSearchResultsLoaded: noop,
      trackSearchSubmit: noop,
      trackSessionStart: noop,
    }
  }

  const { locale } = useNuxtApp().$i18n
  const userStore = useUserStore()
  const $constructor = useNuxtApp().$constructor
  const { getAttributedTerm } = useSearchAttribution()

  const client = $constructor({
    locale: locale.value,
    userId: userStore.user?.customerId || userStore.guestUser?.customerId,
    isMember: userStore.isLoggedIn,
  })

  const trackAutocompleteSelect = async (term: string, param: AutocompleteSelectParam) => {
    try {
      await client.tracker.trackAutocompleteSelect(term, param)
    } catch (e) {
      console.error('[Constructor] Cannot trackAutocompleteSelect', e)
    }
  }

  const trackBrowseResultClick = async (param: BrowseResultClickParam) => {
    try {
      await client.tracker.trackBrowseResultClick(param)
    } catch (e) {
      console.error('[Constructor] Cannot trackBrowseResultClick', e)
    }
  }

  const trackBrowseResultsLoaded = async (param: BrowseResultsLoadedParam) => {
    try {
      await client.tracker.trackBrowseResultsLoaded(param)
    } catch (e) {
      console.error('[Constructor] Cannot trackBrowseResultsLoaded', e)
    }
  }

  const trackConversion = async (product: FlattenedProduct | ProductItemWithProductData) => {
    try {
      const itemParam = getItemParam(product)
      const param = {
        ...itemParam,
        revenue: product.price,
        displayName: 'Add to Cart',
        type: 'add_to_cart',
        isCustomType: true,
      }

      // Sending a term the shopper didn't arrive through would credit the sale to an unrelated search.
      await client.tracker.trackConversion(getAttributedTerm([itemParam.itemId, itemParam.variationId]), param)
    } catch (e) {
      console.error('[Constructor] Cannot trackConversion', e)
    }
  }

  const trackWishlistConversion = async (itemParam: RequiredItemParam) => {
    try {
      const param = {
        ...itemParam,
        displayName: 'Add to Wishlist',
        type: 'add_to_wishlist',
        isCustomType: true,
      }

      await client.tracker.trackConversion(getAttributedTerm([itemParam.itemId, itemParam.variationId]), param)
    } catch (e) {
      console.error('[Constructor] Cannot trackWishlistConversion', e)
    }
  }

  const trackItemDetailLoad = async (product: FlattenedProduct | ProductItemWithProductData, url: string) => {
    try {
      const param = { ...getItemParam(product), url }
      await client.tracker.trackItemDetailLoad(param)
    } catch (e) {
      console.error('[Constructor] Cannot trackItemDetailLoad', e)
    }
  }

  const trackPurchase = async (order: OrderWithProductData | null) => {
    try {
      const param = {
        orderId: order?.orderNo,
        revenue: order?.orderTotal || 0,
        items:
          order?.productItems?.map((item) => ({
            ...getItemParam(item),
            price: item.priceAfterItemDiscount || item.price,
            count: item.quantity,
          })) || [],
      }

      await client.tracker.trackPurchase(param)
    } catch (e) {
      console.error('[Constructor] Cannot trackPurchase', e)
    }
  }

  const trackRecommendationClick = async (param: RecommendationClickParam) => {
    try {
      await client.tracker.trackRecommendationClick(param)
    } catch (e) {
      console.error('[Constructor] Cannot trackRecommendationClick', e)
    }
  }

  const trackRecommendationView = async (param: RecommendationViewParam) => {
    try {
      await client.tracker.trackRecommendationView(param)
    } catch (e) {
      console.error('[Constructor] Cannot trackRecommendationView', e)
    }
  }

  const trackSearchResultClick = async (term: string, param: SearchResultClickParam) => {
    try {
      await client.tracker.trackSearchResultClick(term, param)
    } catch (e) {
      console.error('[Constructor] Cannot trackSearchResultClick', e)
    }
  }

  const trackSearchResultsLoaded = async (term: string, param: SearchResultsLoadedParam) => {
    try {
      await client.tracker.trackSearchResultsLoaded(term, param)
    } catch (e) {
      console.error('[Constructor] Cannot trackSearchResultsLoaded', e)
    }
  }

  const trackSearchSubmit = async (term: string, param: SearchSubmitParam) => {
    try {
      await client.tracker.trackSearchSubmit(term, param)
    } catch (e) {
      console.error('[Constructor] Cannot trackSearchSubmit', e)
    }
  }

  return {
    // NOTE: Generally don't await these data layer functions to avoid slowing UI
    trackAutocompleteSelect,
    trackBrowseResultClick,
    trackBrowseResultsLoaded,
    trackConversion,
    trackWishlistConversion,
    trackInputFocus: () => client.tracker.trackInputFocus(),
    trackItemDetailLoad,
    trackPurchase,
    trackRecommendationClick,
    trackRecommendationView,
    trackSearchResultClick,
    trackSearchResultsLoaded,
    trackSearchSubmit,
    trackSessionStart: () => client.tracker.trackSessionStart(),
  }
}
